import capitalize from "lodash/capitalize";
import { ReactNode } from "react";
import { IDaysRangeRule } from "../../context-providers";
import { FileHelper } from "../../utils/file-helper"; // import directly to avoid circular dependency

export const ERROR_MESSAGES = {
	COMMON: {
		FIELD_REQUIRED: "This field is required.",
		REQUIRED_OPTION: "An option is required.",
		REQUIRED_OPTIONS: "Both options are required.",
	},
	CONTACT: {
		INVALID_SINGAPORE_NUMBER: "Invalid Singapore number.",
		INVALID_INTERNATIONAL_NUMBER: "Invalid international number.",
	},
	NUMERIC: {
		INVALID_DECIMALS: (decimals: number) => `Up to ${decimals} decimal place${decimals === 1 ? "" : "s"} only.`,
	},
	DATE: {
		MUST_BE_FUTURE: "Date must be in the future.",
		MUST_BE_PAST: "Date must be in the past.",
		CANNOT_BE_FUTURE: "Date cannot be in the future.",
		CANNOT_BE_PAST: "Date cannot be in the past.",
		MIN_DATE: (date: string) => `Date cannot be earlier than ${date}.`,
		MAX_DATE: (date: string) => `Date cannot be later than ${date}.`,
		DISABLED_DATES: "Date is not allowed.",
		INVALID: "Invalid date.",
		WITHIN_DAYS: (withinDays: IDaysRangeRule) => {
			const { numberOfDays, fromDate } = withinDays;
			const unsignedDays = Math.abs(numberOfDays);
			return `Date must be within ${unsignedDays} ${unsignedDays !== 1 ? "days" : "day"} ${
				numberOfDays > 0 ? "from" : "prior to"
			} ${fromDate ? ` ${fromDate}` : " today"}.`;
		},
		BEYOND_DAYS: (beyondDays: IDaysRangeRule) => {
			const { numberOfDays, fromDate } = beyondDays;
			const unsignedDays = Math.abs(numberOfDays);
			return `Date must be beyond ${unsignedDays} ${unsignedDays !== 1 ? "days" : "day"} ${
				numberOfDays > 0 ? "from" : "prior to"
			} ${fromDate ? ` ${fromDate}` : " today"}.`;
		},
	},
	DATE_RANGE: {
		MUST_BE_FUTURE: "Dates must be in the future.",
		MUST_BE_PAST: "Dates must be in the past.",
		CANNOT_BE_FUTURE: "Dates cannot be in the future.",
		CANNOT_BE_PAST: "Dates cannot be in the past.",
		MIN_DATE: (date: string) => `Dates cannot be earlier than ${date}.`,
		MAX_DATE: (date: string) => `Dates cannot be later than ${date}.`,
		DISABLED_DATES: "Date range should not include disabled dates.",
		INVALID: "Invalid dates",
		REQUIRED: "Both dates are required",
		MUST_HAVE_NUMBER_OF_DAYS: (numberOfDays: number) => `Selection should have ${numberOfDays} days.`,
	},
	EMAIL: {
		INVALID: "Invalid email address.",
	},
	ESIGNATURE: {
		UPLOAD: "Signature not uploaded.",
	},
	GENERIC: {
		INVALID: "Invalid input",
		UNSUPPORTED: "This component is not supported by the engine.",
	},
	OTP_VERIFICATION: {
		EMAIL_VERIFICATION_REQUIRED: "Please verify your email.",
		PHONE_VERIFICATION_REQUIRED: "Please verify your contact number.",
		OTP_REQUIRED: "OTP is required.",
		INVALID_EMAIL: "Enter a valid email address.",
		INVALID_PHONE: "Enter a valid contact number.",
		INVALID_OTP: "Wrong OTP. Please try again.",
		SEND_OTP_FAILED: "Failed to send OTP. Please try again.",
		OTP_VERIFICATION_FAILED: "Something went wrong. Please request another OTP.",
	},
	UPLOAD: (unit = "file", unitPlural = `${unit}s`) => ({
		REQUIRED: `Upload at least 1 ${unit}.`,
		MAX_FILES: (max: number) =>
			`Upload failed. You can only upload a maximum of ${max} ${max !== 1 ? unitPlural : unit}.`,
		MAX_FILES_WITH_REMAINING: (remaining: number) =>
			`Upload failed. You can only upload ${remaining} more ${
				remaining === 1 ? unit : unitPlural
			}. To upload more ${unitPlural}, you may wish to delete your previously uploaded ${unitPlural}.`,
		MAX_FILE_SIZE: (maxSize: number) =>
			`Upload failed. ${capitalize(unit)} exceeds the maximum size of ${maxSize} KB.`,
		GENERIC: "Upload failed. Please try again.",
		FILE_TYPE: (acceptedFileTypes: string[]) =>
			`Upload failed. Only ${FileHelper.extensionsToSentence(acceptedFileTypes, {
				setBothJpegAndJpgIfEitherExists: true,
			})} files are accepted.`,
		FILE_EXTENSION: (fileExtensions: string[]) =>
			`Upload failed. Only files with the following file extensions are accepted: ${FileHelper.extensionsToSentence(
				fileExtensions
			)}.`,
		UPLOADING: `Please wait for all ${unitPlural} to finish uploading.`,
		MODAL: {
			FILE_TYPE: {
				TITLE: `Unsupported ${unit} format`,
				DESCRIPTION: (filename: ReactNode, acceptedFileTypes: string[]) => (
					<>
						{filename} could not be uploaded. Try again with a{" "}
						{FileHelper.extensionsToSentence(acceptedFileTypes)} file.
					</>
				),
			},
			GENERIC_ERROR: {
				TITLE: `${capitalize(unit)} not uploaded`,
				DESCRIPTION: (filename: ReactNode) => <>There was a problem uploading {filename}. Please try again.</>,
				INVALID_FILE_NAME: `Invalid ${unit} name`,
			},
			MAX_FILE_SIZE: {
				TITLE: `${capitalize(unit)} exceeds maximum size`,
				DESCRIPTION: (filename: ReactNode, maxSize: number) => (
					<>
						{filename} exceeds the maximum size of {maxSize} KB.
					</>
				),
			},
		},
	}),
	SLIDER: {
		MUST_BE_INCREMENTAL: "Input must be incremental.",
	},
	UNIT_NUMBER: {
		INVALID: "Invalid unit number.",
	},
	LOCATION: {
		MUST_HAVE_POSTAL_CODE: "Selected location must have postal code.",
	},
	ARRAY_FIELD: {
		INVALID: "One or more of the sections is incomplete",
		REQUIRED: "At least one section must be filled in",
		MIN: (min: number) => `At least ${min} ${min === 1 ? "entry" : "entries"} must be provided`,
		MAX: (max: number) => `No more than ${max} ${max === 1 ? "entry" : "entries"} can be provided`,
		LENGTH: (length: number) => `Exactly ${length} ${length === 1 ? "entry" : "entries"} must be provided`,
		UNIQUE: "One or more fields are not unique",
	},
};
